import { useCallback } from 'react';
import classNames from 'classnames';

import NextImage from '@com/_core/NextImage';
import { Level3ProductsDataModel } from '@com/_molecules/OtcProductsSlider';
import Counter from '../shared/counter';
import CatalogLink from '@com/seo/CatalogLink';
import { buildPdpPath, getProductId } from '@utilities/catalog/paths';
import { ChatbotProductsResponse } from '@utilities/interfaces/other';
import Badge from '@com/shared/badge';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import BadgeLabel from '@com/shared/cards/badgeLabel';
import Chips from '@com/shared/Chips';
import { convertRialToTomanNumber } from '@utilities/mainUtils';

type PromotionProductDataModel = {
  carouselId?: number;
  discountPercent?: number;
  discountPrice?: number;
  genericCode?: string;
  imageLink?: string;
  maxOrderLimit?: number;
  productName?: string;
  quantity?: number;
  categoryCode?: number;
  shortDescription?: string | null;
  brandName?: string;
  categoryCodeLevel3?: string;
  productType?: number;
  isRare?: boolean;
  price?: number;
};

type ProductDataModel = Level3ProductsDataModel &
  PromotionProductDataModel &
  ChatbotProductsResponse;

interface VerticalCardProps {
  product: ProductDataModel;
  onClick?: () => void;
  hasAddToCart?: boolean;
  className?: string;
}
const MainVerticalCard = ({
  product,
  onClick,
  hasAddToCart = true,
  className,
}: VerticalCardProps) => {
  const href = buildPdpPath(getProductId(product), product?.productName);
  const onClickHandler = useCallback(
    (e) => {
      e.stopPropagation();
      onClick?.();
    },
    [onClick],
  );

  const isShopProduct = !!product?.price;
  const sharedClassName = classNames(
    className,
    'bg-white rounded-lg px-1.5 py-2 shadow-fab flex flex-col',
    isShopProduct ? 'h-[260px] w-[160px]' : 'h-[214px] w-[124px]',
  );
  const cardContent = (
    <>
      <div className="relative w-full flex justify-center items-center">
        <div className="h-28 w-28 relative rounded-lg flex justify-center items-center shadow-sm overflow-hidden bg-surface-primary">
          <NextImage
            src={product?.imageLink}
            alt={product?.productName || 'تصویر محصول'}
            width={112}
            height={112}
          />
          {hasAddToCart ? <Counter product={product} /> : null}
        </div>
        <div>
          {product?.isRare ? (
            <BadgeLabel
              text="داروی‌کمیاب"
              iconName="Spark"
              className="right-0 top-1"
            />
          ) : (
            <div className="absolute right-0 top-1 flex items-center gap-x-[2px] bg-surface-warning rounded-full h-4 px-1 shadow-fab">
              <Icon
                width={0.75}
                height={0.75}
                fill={colors?.black}
                name="LightningBolt"
              />
              <span className="text-Label/2xSmall text-content-onWarning">
                ارسال فوری
              </span>
            </div>
          )}
        </div>
      </div>

      {product?.slangs?.length > 0 && (
        <div className="min-h-[22px] mt-2 text-xs w-full flex overflow-auto">
          <div className="flex flex-nowrap gap-x-0.5">
            {product.slangs.map((item, index) => (
              <Badge
                key={index}
                className="!text-Label/2xSmall h-[22px]"
                color="blue"
              >
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="text-Label/2xSmall mt-2">
        <p
          className={classNames(
            'line-clamp-3 max-h-12',
            isShopProduct ? 'h-8' : 'h-12',
          )}
        >
          {product?.productName}
        </p>
      </div>

      {isShopProduct && (
        <div
          className={classNames(
            'w-full mt-2 flex gap-x-1',
            !!product?.discountPercent ? 'justify-between' : 'justify-end',
          )}
        >
          {!!product?.discountPercent && (
            <Chips
              text={`٪ ${product?.discountPercent}`}
              className="w-max h-6 px-2 bg-surface-warning text-content-primary text-Label/xSmall "
            />
          )}

          <div className="flex flex-col items-end">
            <div className="flex items-center gap-x-1">
              <span className="text-content-primary text-Label/Medium">
                {!!product?.discountPercent
                  ? convertRialToTomanNumber(
                      product?.discountPrice,
                    ).toLocaleString('fa-IR')
                  : convertRialToTomanNumber(product?.price).toLocaleString(
                      'fa-IR',
                    )}
              </span>
              <span className="text-Label/xSmall text-content-primary">
                تومان
              </span>
            </div>

            {!!product?.discountPercent && (
              <div className="flex items-center gap-x-1">
                <span className="text-content-tertiary text-Label/Medium line-through">
                  {convertRialToTomanNumber(product?.price).toLocaleString(
                    'fa-IR',
                  )}
                </span>
                <span className="text-Label/xSmall text-content-tertiary">
                  تومان
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className={classNames('relative', sharedClassName)}>
      {onClick ? (
        <div
          onClick={onClickHandler}
          className="absolute inset-0 z-[1]"
        />
      ) : (
        <CatalogLink
          href={href}
          className="absolute inset-0 z-[1]"
          ariaLabel={product?.productName}
        >
          <span className="sr-only">{product?.productName}</span>
        </CatalogLink>
      )}
      {cardContent}
    </div>
  );
};

export default MainVerticalCard;
